/**
 * TimeclockClient exposes T&A scheduling methods (#1278 #1285).
 * It is composed as `LumoraClient.timeclock` (not inherited) so it does not
 * disturb the existing OpsClient / InspectionsClient inheritance chain.
 * Self-service routes require CapTimeclockSelf; manager routes require CapTimeclockManage.
 */
export class TimeclockClient {
    req;
    constructor(req) {
        this.req = req;
    }
    static query(params) {
        const q = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
            if (v !== undefined && v !== "")
                q.set(k, v);
        }
        const s = q.toString();
        return s ? `?${s}` : "";
    }
    /**
     * GET /v1/timeclock/me
     * Returns clock status + today's published shifts for the authenticated staff member.
     * Requires CapTimeclockSelf.
     */
    async timeclockMe() {
        return this.req("GET", "/v1/timeclock/me", undefined, true);
    }
    /**
     * GET /v1/timeclock/schedule
     * Lists shift slots (draft + published) in a date range.
     * Requires CapTimeclockManage.
     */
    async listShiftSlots(params) {
        const q = TimeclockClient.query({
            from: params.from,
            to: params.to,
            ...(params.publishedOnly !== undefined ? { publishedOnly: String(params.publishedOnly) } : {}),
        });
        const res = await this.req("GET", `/v1/timeclock/schedule${q}`, undefined, true);
        return res.slots;
    }
    /**
     * POST /v1/timeclock/schedule
     * Creates a new shift slot.
     * Requires CapTimeclockManage.
     */
    async createShiftSlot(input) {
        return this.req("POST", "/v1/timeclock/schedule", input, true);
    }
    /**
     * PATCH /v1/timeclock/schedule/{id}
     * Partially updates an existing shift slot.
     * Requires CapTimeclockManage.
     */
    async patchShiftSlot(id, patch) {
        return this.req("PATCH", `/v1/timeclock/schedule/${encodeURIComponent(id)}`, patch, true);
    }
    /**
     * DELETE /v1/timeclock/schedule/{id}
     * Removes a shift slot.
     * Requires CapTimeclockManage.
     */
    async deleteShiftSlot(id) {
        await this.req("DELETE", `/v1/timeclock/schedule/${encodeURIComponent(id)}`, undefined, true);
    }
    /**
     * POST /v1/timeclock/schedule/{year}/{week}/publish
     * Publishes all draft slots in an ISO week, notifying staff.
     * Requires CapTimeclockManage.
     */
    async publishScheduleWeek(year, week) {
        return this.req("POST", `/v1/timeclock/schedule/${encodeURIComponent(year)}/${encodeURIComponent(week)}/publish`, {}, true);
    }
    /**
     * GET /v1/timeclock/schedule/gradient?week=YYYY-Www
     * Returns the 7×24 open/closed/buffer availability overlay for a calendar week.
     * Requires CapTimeclockManage.
     */
    async weekGradient(week) {
        const q = TimeclockClient.query({ week });
        return this.req("GET", `/v1/timeclock/schedule/gradient${q}`, undefined, true);
    }
    // --- US-01 self-service routes (#1272) ---
    /**
     * GET /v1/timeclock/me/status
     * Returns the authenticated staff member's own clock status (clocked in / out / on break).
     * Requires CapTimeclockSelf.
     */
    async clockSelfStatus() {
        return this.req("GET", "/v1/timeclock/me/status", undefined, true);
    }
    /**
     * POST /v1/timeclock/me/punch
     * Records a clock-in, clock-out, break-start or break-end for the authenticated staff member.
     * Requires CapTimeclockSelf.
     */
    async selfPunch(input) {
        return this.req("POST", "/v1/timeclock/me/punch", input, true);
    }
    // --- US-05 availability (#1276) ---
    /**
     * GET /v1/timeclock/me/availability
     * Returns the authenticated staff member's recurring weekly availability.
     * Requires CapTimeclockSelf.
     */
    async getMyAvailability() {
        return this.req("GET", "/v1/timeclock/me/availability", undefined, true);
    }
    /**
     * PUT /v1/timeclock/me/availability
     * Replaces the authenticated staff member's full recurring weekly availability.
     * Returns the saved week plus a non-retroactive disclaimer string.
     * Requires CapTimeclockSelf.
     */
    async setMyAvailability(input) {
        return this.req("PUT", "/v1/timeclock/me/availability", input, true);
    }
    /**
     * GET /v1/timeclock/availability?week=YYYY-Www
     * Returns recurring weekly availability for all staff on the tenant.
     * The week param is echoed but availability is recurring (not week-specific).
     * Requires CapTimeclockManage.
     */
    async listAvailability(week) {
        const q = TimeclockClient.query({ week });
        return this.req("GET", `/v1/timeclock/availability${q}`, undefined, true);
    }
    // --- US-06 time-off requests (#1277) ---
    /**
     * GET /v1/timeclock/me/time-off
     * Lists the authenticated staff member's own time-off requests.
     * Requires CapTimeclockSelf.
     */
    async listMyTimeOff(params) {
        const q = TimeclockClient.query({
            from: params?.from,
            to: params?.to,
            status: params?.status,
        });
        const res = await this.req("GET", `/v1/timeclock/me/time-off${q}`, undefined, true);
        return res.requests;
    }
    /**
     * POST /v1/timeclock/me/time-off
     * Creates a pending time-off request for the authenticated staff member.
     * Requires CapTimeclockSelf.
     */
    async requestTimeOff(input) {
        return this.req("POST", "/v1/timeclock/me/time-off", input, true);
    }
    /**
     * DELETE /v1/timeclock/me/time-off/{id}
     * Cancels a pending time-off request for the authenticated staff member.
     * Requires CapTimeclockSelf.
     */
    async cancelMyTimeOff(id) {
        return this.req("DELETE", `/v1/timeclock/me/time-off/${encodeURIComponent(id)}`, undefined, true);
    }
    /**
     * GET /v1/timeclock/time-off
     * Lists all time-off requests across all staff (manager view).
     * Requires CapTimeclockManage.
     */
    async listAllTimeOff(params) {
        const q = TimeclockClient.query({
            from: params?.from,
            to: params?.to,
            status: params?.status,
        });
        const res = await this.req("GET", `/v1/timeclock/time-off${q}`, undefined, true);
        return res.requests;
    }
    /**
     * POST /v1/timeclock/time-off
     * Manager creates a time-off request on behalf of a staff member; auto-approved.
     * Requires CapTimeclockManage.
     */
    async managerCreateTimeOff(input) {
        return this.req("POST", "/v1/timeclock/time-off", input, true);
    }
    /**
     * POST /v1/timeclock/time-off/{id}/approve
     * Approves a pending time-off request.
     * Requires CapTimeclockManage.
     */
    async approveTimeOff(id) {
        return this.req("POST", `/v1/timeclock/time-off/${encodeURIComponent(id)}/approve`, {}, true);
    }
    /**
     * POST /v1/timeclock/time-off/{id}/reject
     * Rejects a pending time-off request.
     * Requires CapTimeclockManage.
     */
    async rejectTimeOff(id) {
        return this.req("POST", `/v1/timeclock/time-off/${encodeURIComponent(id)}/reject`, {}, true);
    }
    // --- #1484 leave balances + iCal feed ---
    /**
     * GET /v1/timeclock/me/leave-balances?year=
     * Accrued / used / remaining leave per time-off kind for the authenticated staff member.
     */
    async myLeaveBalances(year) {
        const q = year ? `?year=${year}` : "";
        return this.req("GET", `/v1/timeclock/me/leave-balances${q}`, undefined, true);
    }
    /**
     * GET /v1/timeclock/leave-balances?staffId=&year=
     * Manager view of one employee's leave balances. Requires CapTimeclockManage.
     */
    async leaveBalances(staffId, year) {
        const q = new URLSearchParams({ staffId });
        if (year)
            q.set("year", String(year));
        return this.req("GET", `/v1/timeclock/leave-balances?${q.toString()}`, undefined, true);
    }
    /**
     * GET /v1/timeclock/me/ical-feed
     * The staff member's iCal feed token and server-relative path (issued on first use).
     */
    async myIcalFeed() {
        return this.req("GET", "/v1/timeclock/me/ical-feed", undefined, true);
    }
    /**
     * POST /v1/timeclock/me/ical-feed/rotate
     * Issues a new feed token; the previous feed URL stops working immediately.
     */
    async rotateMyIcalFeed() {
        return this.req("POST", "/v1/timeclock/me/ical-feed/rotate", {}, true);
    }
    // --- #1484 shift swaps ---
    /**
     * POST /v1/timeclock/shift-swaps
     * Offers one of the caller's own published shifts: trade it for `targetSlotId`, or give it away when omitted.
     * Requires CapTimeclockSelf.
     */
    async requestShiftSwap(input) {
        return this.req("POST", "/v1/timeclock/shift-swaps", input, true);
    }
    /**
     * GET /v1/timeclock/me/shift-swaps
     * Swaps the caller started, was asked to take, or could still pick up (open give-aways).
     */
    async myShiftSwaps() {
        const res = await this.req("GET", "/v1/timeclock/me/shift-swaps", undefined, true);
        return res.requests;
    }
    /** POST /v1/timeclock/shift-swaps/{id}/accept — the target coworker (anyone but the requester for a give-away). */
    async acceptShiftSwap(id) {
        return this.req("POST", `/v1/timeclock/shift-swaps/${encodeURIComponent(id)}/accept`, {}, true);
    }
    /** POST /v1/timeclock/shift-swaps/{id}/cancel — the requester withdraws an open swap. */
    async cancelShiftSwap(id) {
        return this.req("POST", `/v1/timeclock/shift-swaps/${encodeURIComponent(id)}/cancel`, {}, true);
    }
    /** GET /v1/timeclock/shift-swaps?status= — every swap in the tenant. Requires CapTimeclockManage. */
    async listShiftSwaps(params = {}) {
        const q = params.status ? `?status=${encodeURIComponent(params.status)}` : "";
        const res = await this.req("GET", `/v1/timeclock/shift-swaps${q}`, undefined, true);
        return res.requests;
    }
    /** POST /v1/timeclock/shift-swaps/{id}/approve — applies the schedule change atomically. Requires CapTimeclockManage. */
    async approveShiftSwap(id) {
        return this.req("POST", `/v1/timeclock/shift-swaps/${encodeURIComponent(id)}/approve`, {}, true);
    }
    /** POST /v1/timeclock/shift-swaps/{id}/decline — refuses an open swap. Requires CapTimeclockManage. */
    async declineShiftSwap(id) {
        return this.req("POST", `/v1/timeclock/shift-swaps/${encodeURIComponent(id)}/decline`, {}, true);
    }
    /**
     * DELETE /v1/timeclock/time-off/{id}
     * Manager cancels a pending or approved time-off request.
     * Requires CapTimeclockManage.
     */
    async managerCancelTimeOff(id) {
        return this.req("DELETE", `/v1/timeclock/time-off/${encodeURIComponent(id)}`, undefined, true);
    }
    // --- US-02/03/04 worked hours, exceptions, staff time card (#1274 #1273 #1275) ---
    /**
     * GET /v1/timeclock/exceptions?from&to
     * Lists tardy / absent / missing_out findings. Omit both params for the current pay period.
     * Requires CapTimeclockManage (or employees:manage).
     */
    async listExceptions(params = {}) {
        const q = TimeclockClient.query({ from: params.from, to: params.to });
        const res = await this.req("GET", `/v1/timeclock/exceptions${q}`, undefined, true);
        return res.exceptions ?? [];
    }
    /**
     * GET /v1/timeclock/me/schedule?from&to
     * The authenticated staff member's own published shifts (window at most 366 days).
     * Requires CapTimeclockSelf.
     */
    async myScheduleSlots(params) {
        const q = TimeclockClient.query({ from: params.from, to: params.to });
        const res = await this.req("GET", `/v1/timeclock/me/schedule${q}`, undefined, true);
        return res.slots ?? [];
    }
    /**
     * GET /v1/timeclock/me/timesheet?period=
     * The authenticated staff member's own time card: totals, shifts, punch log, exceptions.
     * Requires CapTimeclockSelf.
     */
    async myTimesheet(period) {
        const q = TimeclockClient.query({ period });
        return this.req("GET", `/v1/timeclock/me/timesheet${q}`, undefined, true);
    }
    // --- US-08 conflict preflight (#1280) ---
    /**
     * GET /v1/timeclock/schedule/conflict-check
     * Read-only preflight: "none" | "availability_warning" | "pto_hard_block".
     * Requires CapTimeclockManage.
     */
    async checkShiftConflict(staffId, startsAt, endsAt) {
        const q = TimeclockClient.query({ staffId, startsAt, endsAt });
        return this.req("GET", `/v1/timeclock/schedule/conflict-check${q}`, undefined, true);
    }
    // --- US-09 recurring patterns (#1279) ---
    /** GET /v1/timeclock/staff/{id}/patterns. Requires CapTimeclockManage. */
    async listShiftPatterns(staffId) {
        const res = await this.req("GET", `/v1/timeclock/staff/${encodeURIComponent(staffId)}/patterns`, undefined, true);
        return res.patterns;
    }
    /** POST /v1/timeclock/staff/{id}/patterns. Requires CapTimeclockManage. */
    async createShiftPattern(staffId, input) {
        return this.req("POST", `/v1/timeclock/staff/${encodeURIComponent(staffId)}/patterns`, input, true);
    }
    /** DELETE /v1/timeclock/staff/{id}/patterns/{patternId}. Requires CapTimeclockManage. */
    async deleteShiftPattern(staffId, patternId) {
        await this.req("DELETE", `/v1/timeclock/staff/${encodeURIComponent(staffId)}/patterns/${encodeURIComponent(patternId)}`, undefined, true);
    }
    /**
     * POST /v1/timeclock/schedule/generate?week=YYYY-Www
     * Pre-fills DRAFT shifts for the week from active patterns (idempotent; PTO-locked
     * days are skipped and reported). Requires CapTimeclockManage.
     */
    async generateWeekFromPatterns(week, staffIds) {
        const q = TimeclockClient.query({ week });
        return this.req("POST", `/v1/timeclock/schedule/generate${q}`, staffIds && staffIds.length > 0 ? { staffIds } : {}, true);
    }
}
//# sourceMappingURL=timeclock.js.map